import { Box, Icon, Typography } from '@/components/muiComponents';
import { LOG_MESSAGES } from '@/constants/logMessages';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { colors } from '@/theme/colors';
import { isDemoEnvironment } from '@/util/demoUtils';
import { logEvent } from '@/util/logger';
import { fullName } from '@/util/util';
import { AccountCircleOutlined, SupportAgent } from '@mui/icons-material';
import {
  Avatar,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Popover,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const AuthButton = ({ closed }: { closed: boolean }) => {
  const isStaff = process.env.REACT_APP_ENV === 'staff';
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openedMenu, setOpenedMenu] = useState<boolean>(false);
  const theme = useTheme();

  const { authState, login, logout } = useAuth();
  const { profile, profileImage } = useProfile();

  const { t } = useTranslation(['auth']);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    logEvent(LOG_MESSAGES.USER_CLICKED_USER_MENU);
    setAnchorEl(event.currentTarget as HTMLButtonElement);
    setOpenedMenu(false);
  };

  const handleClose = () => {
    logEvent(LOG_MESSAGES.USER_CLOSED_USER_MENU);
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const isDemo = isDemoEnvironment();

  let name = authState?.isAuthenticated ? `${fullName(profile)}` : '';
  name = isDemo ? 'Demo User' : name;

  const menuPopout = () => (
    <Box
      id="auth-menu"
      data-testid="auth-menu"
      role="menu"
      aria-orientation="vertical"
      aria-label={
        authState?.isAuthenticated ? 'User menu' : 'Authentication menu'
      }
      sx={{
        position: 'absolute',
        top: '0px',
        left: '100%',
        display: {
          xs: 'none',
          lg: openedMenu && closed ? 'block' : 'none',
        },
        p: '4px',
        borderRadius: '8px',
      }}
    >
      <ListItem disablePadding role="menuitem">
        <ListItemButton
          data-testid="auth-menu-button"
          sx={{
            padding: '12px',
            borderRadius: 3,
            // backgroundColor: theme.palette.tooltips.main,
            transition: 'all 0.3s ease',
          }}
          onClick={authState?.isAuthenticated ? logout : login}
          aria-label={
            authState?.isAuthenticated ? `Sign out as ${name}` : 'Sign in'
          }
        >
          <Typography
            variant="bodyMedium"
            sx={{
              color: theme.palette.background.default,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {authState?.isAuthenticated ? `${name}` : 'Sign In'}
          </Typography>
        </ListItemButton>
      </ListItem>
    </Box>
  );

  if (!authState?.isAuthenticated) {
    return (
      <Box
        data-testid="auth-signin-container"
        width="100%"
        display="flex"
        justifyContent="start"
        position="relative"
        alignItems="center"
        mt={2}
        mb={1.5}
        onMouseEnter={() => setOpenedMenu(true)}
        onMouseLeave={() => setOpenedMenu(false)}
      >
        <ListItemButton
          data-testid="auth-signin-button"
          sx={{
            padding: '12px',
            borderRadius: 3,
          }}
          onClick={login}
          aria-haspopup="menu"
          aria-expanded={openedMenu && closed}
          aria-controls="auth-menu"
        >
          <Box
            display="flex"
            justifyContent="start"
            alignItems="center"
            sx={{ minWidth: 28 }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Icon
                icon={AccountCircleOutlined}
                sx={{ p: '2px' }}
                size="28px"
              />
            </ListItemIcon>
          </Box>
          {!closed && (
            <Typography variant="bodyMedium">{t('signIn')}</Typography>
          )}
        </ListItemButton>
        {menuPopout()}
      </Box>
    );
  }

  return (
    <Box
      data-testid="auth-user-container"
      width="100%"
      display="flex"
      justifyContent="start"
      alignItems="center"
      position="relative"
      mt={2}
      mb={1.5}
      onMouseEnter={() => setOpenedMenu(true)}
      onMouseLeave={() => setOpenedMenu(false)}
    >
      <ListItemButton
        data-testid="auth-user-button"
        sx={{
          display: 'flex',
          alignItems: 'center',
          paddingY: '12px',
          paddingX: !closed ? '12px' : '10px',
          borderRadius: 3,
          transition: 'all .5s ease',
        }}
        onClick={handleClick}
        aria-haspopup="menu"
        aria-expanded={openedMenu && closed}
        aria-controls="auth-menu"
      >
        {isDemo ? (
          <Icon icon={SupportAgent} size="32px" />
        ) : (
          <Avatar
            sx={{
              backgroundColor: theme.palette.secondary.main,
              color: 'white',
              height: '32px',
              width: '32px',
            }}
            src={profileImage}
          >
            {name?.charAt(0) || ''}
          </Avatar>
        )}
        {!closed && (
          <Typography
            variant="bodyMedium"
            sx={{
              fontWeight: 'bold',
              marginLeft: '10px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {name}
          </Typography>
        )}
      </ListItemButton>
      {!isStaff && (
        <Popover
          id={id}
          open={open}
          sx={{
            top: '-30px',
            boxShadow: 'none',
            // backgroundColor: 'white', // Ensure the background is explicitly white
            // '& .MuiPaper-root': {
            //   // Ensures the popover container is also white
            //   backgroundColor: 'white',
            // },
            marginLeft: '50px',
          }}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <ListItemButton
            data-testid="auth-logout-button"
            onClick={logout}
            sx={{
              padding: '12px ',
              boxShadow: 'none',
              backgroundColor: theme.palette.tooltips.main,
              transition: 'all 0.3s ease',
              ':hover': {
                backgroundColor: colors.listItemButton.dark.hover,
                color:
                  theme.palette.mode === 'dark' ? colors.white : colors.black,
              },
              color:
                theme.palette.mode === 'dark' ? colors.black : colors.white,
            }}
          >
            {t('logOut') as string}
          </ListItemButton>
        </Popover>
      )}
      {menuPopout()}
    </Box>
  );
};
